import { useEffect, useRef } from 'react'
import { type Types } from '@cornerstonejs/core'
import {
  createSVGOverlay,
  createPHIBox,
  createResizeHandles,
  clearOverlays,
  findBoxById,
} from '../../../utils/svgUtils'
import { convertImageToCanvasCoords, convertCanvasToImageCoords } from '../../../utils/coordinateTransforms'
import { CORNERSTONE_CONFIG, DRAWING_CONFIG, POLLING_INTERVALS } from '../../../constants/phi'
import type {
  PHIPrediction,
  InstanceMetadata,
  DragState,
  SVGOverlayWithObserver,
  EditingBox,
  MouseMoveHandlerWithTime,
} from '../../../types/phi'
import { RenderingEngine } from '@cornerstonejs/core'

interface UsePHIBoxRenderingProps {
  currentImageId: string | null
  elementRef: React.RefObject<HTMLDivElement>
  engine: RenderingEngine | null
  phiPredictions: Record<string, PHIPrediction[]>
  urlToMetadataMap: Record<string, InstanceMetadata>
  redactedSeries: Set<string>
  redactedSOPInstances: Record<string, Set<string>>
  selectedSeries: string
  isEditMode: boolean
  showRedactions: boolean
  currentEditingBox: EditingBox | null
  onSetBoxesDrawn: (drawn: boolean) => void
  onBoxSelect: (sopInstanceUid: string, index: number) => void
  onUpdatePrediction: (sopInstanceUid: string, index: number, updates: Partial<PHIPrediction>) => void
}

export function usePHIBoxRendering({
  currentImageId,
  elementRef,
  engine,
  phiPredictions,
  urlToMetadataMap,
  redactedSeries,
  redactedSOPInstances,
  selectedSeries,
  isEditMode,
  showRedactions,
  currentEditingBox,
  onSetBoxesDrawn,
  onBoxSelect,
  onUpdatePrediction,
}: UsePHIBoxRenderingProps) {
  // Main effect to handle PHI box rendering
  useEffect(() => {
    console.log('PHI Box Rendering Effect:', {
      currentImageId,
      showRedactions,
      phiPredictionsCount: Object.keys(phiPredictions).length,
      selectedSeries,
      isEditMode,
    })

    if (currentImageId && showRedactions) {
      clearOverlays(elementRef.current!)
      drawPHIBoxesAsSVG()
      onSetBoxesDrawn(true)
    } else {
      clearOverlays(elementRef.current!)
      onSetBoxesDrawn(false)
    }
    // drawPHIBoxesAsSVG is excluded: non-memoized fn recreated each render, adding it
    // causes an infinite loop. elementRef and onSetBoxesDrawn are stable refs/callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentImageId,
    // Add back essential dependencies for edit functionality
    phiPredictions,
    isEditMode,
    currentEditingBox,
    selectedSeries,
    urlToMetadataMap,
    // Remove problematic dependencies temporarily to stop infinite re-renders
    // redactedSeries,
    // redactedSOPInstances,
    showRedactions,
  ])

  // Window resize handler: engine.resize() must be called manually in CornerstoneJS v2
  // (no built-in ResizeObserver), then boxes must be redrawn with correct worldToCanvas()
  // coordinates.
  //
  // The "latest ref" pattern is used here instead of listing all drawPHIBoxesAsSVG
  // dependencies in the effect: the ref is updated on every render so the debounced
  // handler always sees the most recent phiPredictions, urlToMetadataMap, etc. without
  // re-registering the window listener on every prediction change (which would cause
  // continuous listener churn and stale-closure bugs on rapid state updates).
  const latestRedrawRef = useRef<(() => void) | null>(null)
  latestRedrawRef.current = () => {
    if (!elementRef.current || !currentImageId || !showRedactions) return
    clearOverlays(elementRef.current)
    drawPHIBoxesAsSVG()
    onSetBoxesDrawn(true)
  }

  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout> | null = null

    const handleWindowResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        if (!engine) return
        // Update CornerstoneJS GPU offscreen canvas and coordinate transform
        engine.resize(true, true)
        // Invoke the latest redraw closure — always has fresh phiPredictions etc.
        latestRedrawRef.current?.()
      }, POLLING_INTERVALS.resizeDelay)
    }

    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
      if (resizeTimer) clearTimeout(resizeTimer)
    }
  }, [engine])

  // Function to draw PHI boxes using SVG overlay
  const drawPHIBoxesAsSVG = () => {
    console.log('drawPHIBoxesAsSVG called')

    if (!currentImageId || !elementRef.current) {
      console.log('Early return: no currentImageId or elementRef')
      return
    }

    // Get the image URL and metadata
    const imageUrl = currentImageId.slice('wadouri:'.length) || ''
    const metadata = urlToMetadataMap[imageUrl]
    console.log('Image metadata lookup:', {
      imageUrl,
      metadata,
      urlToMetadataMapKeys: Object.keys(urlToMetadataMap).length,
      hasMetadata: !!metadata,
    })

    if (!metadata?.SOPInstanceUID) {
      console.error('Could not find SOP Instance UID for image:', imageUrl)
      console.log('Available metadata keys:', Object.keys(urlToMetadataMap))
      console.log('Current image ID:', currentImageId)

      // Try to find a close match in the urlToMetadataMap
      const possibleKeys = Object.keys(urlToMetadataMap).filter(
        (key) => key.includes('example_bone') || key.includes(selectedSeries.substring(selectedSeries.length - 10)),
      )
      console.log('Possible matching keys:', possibleKeys)

      return
    }

    const sopInstanceUid = metadata.SOPInstanceUID
    console.log('Using SOP Instance UID:', sopInstanceUid)

    // Check if this image should be skipped
    if (
      redactedSeries.has(selectedSeries) ||
      (redactedSOPInstances[selectedSeries] && redactedSOPInstances[selectedSeries].has(sopInstanceUid))
    ) {
      clearOverlays(elementRef.current)
      console.log('Skipping redacted series/SOP')
      return
    }

    const predictions = phiPredictions[sopInstanceUid] || []
    console.log('PHI predictions for this image:', predictions.length)

    const viewport = engine?.getViewport(CORNERSTONE_CONFIG.viewportId) as Types.IStackViewport
    if (!viewport) {
      console.error('No viewport found')
      return
    }

    const element = viewport.element
    if (!element) {
      console.error('No viewport element found')
      return
    }

    // Get viewport dimensions
    const viewportRect = element.getBoundingClientRect()
    const viewportWidth = viewportRect.width
    const viewportHeight = viewportRect.height
    console.log('Viewport dimensions:', { viewportWidth, viewportHeight })

    // Create SVG overlay
    const svgOverlay = createSVGOverlay(viewportWidth, viewportHeight, 'phi-overlay', isEditMode)
    element.appendChild(svgOverlay)
    console.log('Created SVG overlay')

    // Initialize drag state for edit mode
    const dragState: DragState = {
      isDragging: false,
      isResizing: false,
      activeBoxId: null,
      startX: 0,
      startY: 0,
      originalX: 0,
      originalY: 0,
      originalWidth: 0,
      originalHeight: 0,
      activeHandle: null,
    }

    // Store drag state in the overlay
    ;(svgOverlay as SVGOverlayWithObserver).dragState = dragState

    // Render each prediction as a PHI box
    predictions.forEach((prediction, index) => {
      console.log(`Rendering PHI box ${index}:`, prediction)
      try {
        // Convert image coordinates to canvas coordinates
        const canvasCoords = convertImageToCanvasCoords(
          prediction.x,
          prediction.y,
          prediction.width,
          prediction.height,
          viewport,
          currentImageId,
        )
        console.log('Canvas coordinates:', canvasCoords)

        // Check if this is the currently selected box
        const isSelected = currentEditingBox?.sopInstanceUid === sopInstanceUid && currentEditingBox?.index === index

        // Create the main PHI box group
        const boxGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
        boxGroup.setAttribute('class', 'phi-box-group')
        boxGroup.setAttribute('id', `phi-box-group-${sopInstanceUid.replace(/\./g, '-')}-${index}`)
        boxGroup.setAttribute('data-sop-instance-uid', sopInstanceUid)
        boxGroup.setAttribute('data-index', index.toString())

        // Create the PHI box rectangle
        const rect = createPHIBox(
          sopInstanceUid,
          index,
          canvasCoords.x,
          canvasCoords.y,
          canvasCoords.width,
          canvasCoords.height,
          prediction.x,
          prediction.y,
          prediction.width,
          prediction.height,
          isSelected,
        )

        console.log('Created PHI box rectangle')

        // Add event handlers for edit mode
        if (isEditMode) {
          rect.onclick = () => onBoxSelect(sopInstanceUid, index)
          rect.style.cursor = 'pointer'
          rect.onmousedown = (e) => handleBoxMouseDown(e, sopInstanceUid, index, dragState)
        }

        boxGroup.appendChild(rect)

        // Add resize handles in edit mode
        if (isEditMode) {
          const handles = createResizeHandles(
            sopInstanceUid,
            index,
            canvasCoords.x,
            canvasCoords.y,
            canvasCoords.width,
            canvasCoords.height,
          )

          handles.forEach((handle) => {
            const handleId = handle.getAttribute('data-handle-id')!
            handle.onmousedown = (e) => handleResizeMouseDown(e, sopInstanceUid, index, handleId, dragState)
            boxGroup.appendChild(handle)
          })
        }

        svgOverlay.appendChild(boxGroup)
        console.log(`PHI box ${index} added to overlay`)
      } catch (error) {
        console.error('Error rendering PHI box:', error)
      }
    })

    console.log(`Finished rendering ${predictions.length} PHI boxes`)

    // Add mouse event handlers for edit mode
    if (isEditMode) {
      const mouseMoveHandler = (e: MouseEvent) => {
        if (dragState.isDragging) {
          handleBoxDrag(e, dragState)
        } else if (dragState.isResizing) {
          handleBoxResize(e, dragState)
        }
      }

      const mouseUpHandler = () => {
        if (dragState.isDragging || dragState.isResizing) {
          handleBoxDragEnd(dragState, viewport, currentImageId, onUpdatePrediction)
        }
      }

      // Store handlers for cleanup
      ;(svgOverlay as SVGOverlayWithObserver).mouseMoveHandler = mouseMoveHandler as MouseMoveHandlerWithTime

      document.addEventListener('mousemove', mouseMoveHandler)
      document.addEventListener('mouseup', mouseUpHandler)
      svgOverlay.addEventListener('mouseleave', mouseUpHandler)
    }
  }

  // Mouse event handlers for edit mode
  const handleBoxMouseDown = (e: MouseEvent, sopInstanceUid: string, index: number, dragState: DragState) => {
    e.stopPropagation()
    onBoxSelect(sopInstanceUid, index)

    const boxElement = findBoxById(sopInstanceUid, index)
    if (!boxElement) return

    dragState.isDragging = true
    dragState.isResizing = false
    dragState.activeBoxId = `phi-box-${sopInstanceUid}-${index}`
    dragState.startX = e.clientX
    dragState.startY = e.clientY
    dragState.originalX = parseFloat(boxElement.getAttribute('x') || '0')
    dragState.originalY = parseFloat(boxElement.getAttribute('y') || '0')
    dragState.originalWidth = parseFloat(boxElement.getAttribute('width') || '0')
    dragState.originalHeight = parseFloat(boxElement.getAttribute('height') || '0')
    dragState.activeHandle = null
  }

  const handleResizeMouseDown = (
    e: MouseEvent,
    sopInstanceUid: string,
    index: number,
    handleId: string,
    dragState: DragState,
  ) => {
    e.stopPropagation()

    const boxElement = findBoxById(sopInstanceUid, index)
    if (!boxElement) return

    dragState.isDragging = false
    dragState.isResizing = true
    dragState.activeBoxId = `phi-box-${sopInstanceUid}-${index}`
    dragState.startX = e.clientX
    dragState.startY = e.clientY
    dragState.originalX = parseFloat(boxElement.getAttribute('x') || '0')
    dragState.originalY = parseFloat(boxElement.getAttribute('y') || '0')
    dragState.originalWidth = parseFloat(boxElement.getAttribute('width') || '0')
    dragState.originalHeight = parseFloat(boxElement.getAttribute('height') || '0')
    dragState.activeHandle = handleId
  }

  const handleBoxDrag = (e: MouseEvent, dragState: DragState) => {
    if (!dragState.isDragging || !dragState.activeBoxId) return

    const boxElement = document.getElementById(dragState.activeBoxId) as unknown as SVGRectElement
    if (!boxElement) return

    const deltaX = e.clientX - dragState.startX
    const deltaY = e.clientY - dragState.startY

    const newX = dragState.originalX + deltaX
    const newY = dragState.originalY + deltaY

    boxElement.setAttribute('x', newX.toString())
    boxElement.setAttribute('y', newY.toString())
  }

  const handleBoxResize = (e: MouseEvent, dragState: DragState) => {
    if (!dragState.isResizing || !dragState.activeBoxId || !dragState.activeHandle) return

    const boxElement = document.getElementById(dragState.activeBoxId) as unknown as SVGRectElement
    if (!boxElement) return

    const deltaX = e.clientX - dragState.startX
    const deltaY = e.clientY - dragState.startY
    const handle = dragState.activeHandle

    let newX = dragState.originalX
    let newY = dragState.originalY
    let newWidth = dragState.originalWidth
    let newHeight = dragState.originalHeight

    if (handle.includes('e')) {
      newWidth = Math.max(DRAWING_CONFIG.minBoxSize, dragState.originalWidth + deltaX)
    }
    if (handle.includes('w')) {
      const widthDelta = Math.min(dragState.originalWidth - DRAWING_CONFIG.minBoxSize, deltaX)
      newX = dragState.originalX + widthDelta
      newWidth = dragState.originalWidth - widthDelta
    }
    if (handle.includes('s')) {
      newHeight = Math.max(DRAWING_CONFIG.minBoxSize, dragState.originalHeight + deltaY)
    }
    if (handle.includes('n')) {
      const heightDelta = Math.min(dragState.originalHeight - DRAWING_CONFIG.minBoxSize, deltaY)
      newY = dragState.originalY + heightDelta
      newHeight = dragState.originalHeight - heightDelta
    }

    boxElement.setAttribute('x', newX.toString())
    boxElement.setAttribute('y', newY.toString())
    boxElement.setAttribute('width', newWidth.toString())
    boxElement.setAttribute('height', newHeight.toString())
  }

  const handleBoxDragEnd = (
    dragState: DragState,
    viewport: Types.IStackViewport,
    currentImageId: string,
    onUpdatePrediction: (sopInstanceUid: string, index: number, updates: Partial<PHIPrediction>) => void,
  ) => {
    if (!dragState.activeBoxId) return

    const boxElement = document.getElementById(dragState.activeBoxId) as unknown as SVGRectElement
    if (!boxElement) return

    // Extract SOP instance UID and index from box ID
    const boxIdParts = dragState.activeBoxId.split('-')
    if (boxIdParts.length < 4) return

    const sopInstanceUid = boxIdParts[2]
    const index = parseInt(boxIdParts[3], 10)

    // Convert canvas coordinates back to image coordinates
    const canvasX = parseFloat(boxElement.getAttribute('x') || '0')
    const canvasY = parseFloat(boxElement.getAttribute('y') || '0')
    const canvasWidth = parseFloat(boxElement.getAttribute('width') || '0')
    const canvasHeight = parseFloat(boxElement.getAttribute('height') || '0')

    try {
      const imageCoords = convertCanvasToImageCoords(
        canvasX,
        canvasY,
        canvasWidth,
        canvasHeight,
        viewport,
        currentImageId,
      )

      // Update the prediction with new coordinates
      onUpdatePrediction(sopInstanceUid, index, {
        x: imageCoords.x,
        y: imageCoords.y,
        width: imageCoords.width,
        height: imageCoords.height,
      })
    } catch (error) {
      console.error('Error updating PHI box coordinates:', error)
    }

    // Reset drag state
    dragState.isDragging = false
    dragState.isResizing = false
    dragState.activeBoxId = null
    dragState.activeHandle = null
  }

  return {
    drawPHIBoxesAsSVG,
  }
}
